import React, { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { Tooltip } from "@mui/material";
import Spinner from "../../Tools/spinner";
import { useNavigate } from "react-router-dom";
import "./ConsolidateDashboardReport.css";
import { toast } from "react-toastify";
import { FaFileExcel } from "react-icons/fa";
import {
  fetchConsolidateDashboard,
  exportConsolidateDashboard,
} from "../../../helper/Api";
import { AddCommasToAmount, downloadAuthBlobResponse, getBlobErrorMessage } from "../../../helper/Utils";
import { DEFAULT_EXPENSE_DATE_BASIS } from "../../../RTK/Slices/AppConfigSlice";

const formatAmount = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  return AddCommasToAmount(value) || "—";
};

const getSelectedFyYear = (fyList, fyId) =>
  fyList?.find((val) => Number(val?.id) === Number(fyId))?.year || "";

const ConsolidateDashboardReport = ({
  embedded = false,
  fyId: fyIdProp,
  fy_list: fyListProp,
  showDashboardLink = false,
}) => {
  const { financialYears } = useSelector((state) => state.authData);
  const expenseDateBasis = useSelector(
    (state) => state.appConfig?.expenseDateBasis || DEFAULT_EXPENSE_DATE_BASIS
  );
  const [expendAll, setExpenddAll] = useState(true);
  const [loading, setLoading] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [amountDivisorTick, setAmountDivisorTick] = useState(0);
  const fy_list = fyListProp || financialYears?.payload?.fy_list || [];
  const [fyId, setFyId] = useState(
    () => fyIdProp || localStorage.getItem("globalDateId")
  );
  const selectedFyYear = getSelectedFyYear(fy_list, fyId);
  const [activeCard, setActiveCard] = useState([]);
  const [subheadTables, setSubheadTables] = useState([]);
  const handleSubheadTable = (id) => {
    setSubheadTables((prev) => {
      let updated = [...prev];
      if (updated.includes(id)) {
        updated = updated.filter((item) => item !== id);
      } else {
        updated.push(id);
      }
      return updated;
    });
  };
  const [schemes, setSchemes] = useState([]);
  const navigate = useNavigate();
  const mainCardSwitch = (val1, val2, val3, detailObj) => {
    setActiveCard((prev) => {
      let updated = [...prev];
      // Remove val2 and val3 if present
      updated = updated.filter((item) => item !== val2 && item !== val3);
      // Toggle val1: close if already open, otherwise open
      if (updated.includes(val1)) {
        updated = updated.filter((item) => item !== val1);
      } else {
        updated.unshift(val1);
      }
      return updated;
    });
  };

  useEffect(() => {
    const onAmountDivisorChange = () => {
      setAmountDivisorTick((prev) => prev + 1);
    };
    window.addEventListener("amount_divisor_changed", onAmountDivisorChange);
    return () =>
      window.removeEventListener("amount_divisor_changed", onAmountDivisorChange);
  }, []);


  const fetchConsolidateData = useCallback(async (financialYearId) => {
    try {
      setLoading(true);
      const response = await fetchConsolidateDashboard({
        financial_year_id: financialYearId,
        expense_date_basis: expenseDateBasis,
      });
      if (response.data.status) {
        setSchemes(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching consolidate data:", error);
    } finally {
      setLoading(false);
    }
  }, [expenseDateBasis]);

  useEffect(() => {
    if (fyIdProp) {
      setFyId(fyIdProp);
    }
  }, [fyIdProp]);

  useEffect(() => {
    if (!fyIdProp) {
      const syncFyFromStorage = () => {
        const nextFyId = localStorage.getItem("globalDateId");
        setFyId((prev) => (prev !== nextFyId ? nextFyId : prev));
      };
      window.addEventListener("storage", syncFyFromStorage);
      window.addEventListener("focus", syncFyFromStorage);
      return () => {
        window.removeEventListener("storage", syncFyFromStorage);
        window.removeEventListener("focus", syncFyFromStorage);
      };
    }
  }, [fyIdProp]);

  useEffect(() => {
    if (fyId) {
      fetchConsolidateData(fyId);
    }
  }, [fyId, fetchConsolidateData]);

  useEffect(() => {
    if (!schemes.length) {
      setActiveCard([]);
      return;
    }

    // Default view: open Sub head section for every scheme
    setActiveCard(schemes.map((scheme) => `subhead_${scheme.id}`));
  }, [schemes]);

  const calculateExpense = (tierFigures) => {
    return tierFigures?.reduce(
      (total, tier) => total + Number(tier.expenses || 0),
      0
    );
  };

  const calculateSchemeExpense = (scheme) => {
    let expense = 0;
    scheme.subheads.forEach((subhead) => {
      expense += calculateExpense(subhead.tierFigures);
    });
    return expense;
  };

  const exportConsolidateReport = async () => {
    setLoading(true);
    try {
      const schemes = await exportConsolidateDashboard({
        financial_year_id: fyId,
        expense_date_basis: expenseDateBasis,
      });
      await downloadAuthBlobResponse(schemes, 'consolidate_dashboard.xlsx');
    } catch (error) {
      toast.error(await getBlobErrorMessage(error, 'Failed to export'), {
        position: toast.POSITION.TOP_CENTER,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={`consolidate-dashboard-report${
        isCollapsed ? " consolidate-dashboard-report--collapsed" : ""
      }${embedded ? " consolidate-dashboard-report--embedded" : ""}`}
    >
      {loading && (
        <div>
          <Spinner />
        </div>
      )}

      <div className="consolidate-dashboard-report__header">
        {!isCollapsed && showDashboardLink ? (
          <div className="consolidate-dashboard-report__header-actions">
            <Tooltip title="Dashboard" arrow>
              <button
                type="button"
                className="consolidate-dashboard-report__header-icon-btn"
                onClick={() => navigate("/")}
                aria-label="Dashboard"
              >
                <i className="icon-home" />
              </button>
            </Tooltip>
          </div>
        ) : null}

        <span className="consolidate-dashboard-report__header-title">
          CONSOLIDATE DASHBOARD
        </span>

        <button
          type="button"
          className="consolidate-dashboard-report__fold-btn"
          onClick={() => setIsCollapsed((prev) => !prev)}
          aria-label={
            isCollapsed
              ? "Expand consolidate dashboard"
              : "Collapse consolidate dashboard"
          }
          title={isCollapsed ? "Expand" : "Collapse"}
        >
          {isCollapsed ? "+" : "−"}
        </button>
      </div>

      {!isCollapsed && (
        <div className="consolidate-dashboard-report__toolbar">
          <Tooltip
            title={expendAll ? "Close All Child" : "Expand All Child"}
            arrow
          >
            <button
              type="button"
              className={`consolidate-dashboard-report__child-toggle${
                expendAll
                  ? " consolidate-dashboard-report__child-toggle--active"
                  : ""
              }`}
              onClick={() => setExpenddAll(!expendAll)}
              aria-label={expendAll ? "Close All Child" : "Expand All Child"}
            >
              <i
                className={
                  expendAll ? "icon-unfold_less" : "icon-unfold_more"
                }
              />
              <span>
                {expendAll ? "Close All Child" : "Expand All Child"}
              </span>
            </button>
          </Tooltip>

          <Tooltip title="Export Excel" arrow>
            <button
              type="button"
              className="consolidate-dashboard-report__export"
              onClick={exportConsolidateReport}
              disabled={loading}
              aria-label="Export Excel"
            >
              <FaFileExcel />
            </button>
          </Tooltip>
        </div>
      )}

      {!isCollapsed && (
        <div className="consolidate-dashboard-report__body" data-amount-tick={amountDivisorTick}>
          <div className="px-3 col-12 d-flex flex-column gap-3 overflow-y-auto overflow-x-hidden consolidate-dashboard-report__scroll">
          {schemes.map((e, index) => (
            <div
              key={e.id || index}
              className="bg-white border-Prime-100 my-2 p-0"
              style={{ borderRadius: "8px" }}
            >
              <div
                className="bg-prime-20 d-flex justify-content-between align-items-center p-2 transition overflow-hidden"
                style={{ borderRadius: "8px 8px 0 0" }}
              >
                {/* ======================card header================== */}
                <span
                  className="fs-14px white-space-nowrap overflow-x-hidden d-flex justify-content-between align-items-center gap-2 p-0 m-0 w-100"
                  style={{ width: "95%", fontStyle: "italic" }}
                >
                  {/* =======================card header====================== */}
                  <span
                    className="fs-14px white-space-nowrap overflow-x-hidden"
                    style={{ width: "95%", fontStyle: "italic" }}
                  >
                    <span className="fw-500 text-secondary-100">
                      Scheme Id {e.code}-{e.name}
                    </span>
                  </span>
                </span>
              </div>

              <div className="consolidate-scheme-metrics">
                <div className="consolidate-subhead-metric consolidate-subhead-metric--soft">
                  <span className="consolidate-subhead-metric__label">
                    Prov. Budget FY {selectedFyYear}
                  </span>
                  <span className="consolidate-subhead-metric__value text-success-100">
                    {formatAmount(e.provisional_budget)}
                  </span>
                </div>
                <div className="consolidate-subhead-metric consolidate-subhead-metric--soft">
                  <span className="consolidate-subhead-metric__label">
                    Released Budget FY {selectedFyYear}
                  </span>
                  <span className="consolidate-subhead-metric__value text-success-100">
                    {formatAmount(e.budget)}
                  </span>
                </div>
                <div className="consolidate-subhead-metric consolidate-subhead-metric--navy">
                  <span className="consolidate-subhead-metric__label">
                    Expense Budget FY {selectedFyYear}
                  </span>
                  <span className="consolidate-subhead-metric__value">
                    {formatAmount(calculateSchemeExpense(e))}
                  </span>
                </div>
                <div className="consolidate-subhead-metric consolidate-subhead-metric--green">
                  <span className="consolidate-subhead-metric__label">
                    Balance FY {selectedFyYear}
                  </span>
                  <span className="consolidate-subhead-metric__value">
                    {formatAmount(e.budget - calculateSchemeExpense(e))}
                  </span>
                </div>
              </div>

              <div className="px-2 m-0">
                <div className="border mb-2"></div>
                <div className="pb-2 d-flex justify-content-between text-secondary-50 px-4">
                  <button
                    onClick={() =>
                      mainCardSwitch(
                        `subhead_${e.id}`,
                        `activity_${e.id}`,
                        `subactivity_${e.id}`,
                        {
                          scheme_id: e.id,
                          subhead: true,
                          activity: false,
                          subActivity: false,
                        }
                      )
                    }
                    className={`border-0 text-secondary-50 d-flex align-items-center gap-1 fs-12px text-secondary-50 px-2 py-1 rounded-4 ${
                      activeCard.includes(`subhead_${e.id}`)
                        ? "bg-secondary-20"
                        : "bg-white"
                    } `}
                  >
                    {activeCard.includes(`subhead_${e.id}`) ? (
                      <i className="icon-remove_circle_outline"></i>
                    ) : (
                      <i className="icon-add_circle_outline"></i>
                    )}
                    Sub head
                  </button>
                  <button
                    onClick={() =>
                      mainCardSwitch(
                        `activity_${e.id}`,
                        `subhead_${e.id}`,
                        `subactivity_${e.id}`,
                        {
                          scheme_id: e.id,
                          subhead: false,
                          activity: true,
                          subActivity: false,
                        }
                      )
                    }
                    className={`border-0 text-secondary-50 d-flex align-items-center gap-1 fs-12px text-secondary-50 px-2 py-1  rounded-4 ${
                      activeCard.includes(`activity_${e.id}`)
                        ? "bg-secondary-20"
                        : "bg-white"
                    } `}
                  >
                    {activeCard.includes(`activity_${e.id}`) ? (
                      <i className="icon-remove_circle_outline"></i>
                    ) : (
                      <i className="icon-add_circle_outline"></i>
                    )}
                    Activity
                  </button>
                  <button
                    onClick={() =>
                      mainCardSwitch(
                        `subactivity_${e.id}`,
                        `activity_${e.id}`,
                        `subhead_${e.id}`,
                        {
                          scheme_id: e.id,
                          subhead: false,
                          activity: false,
                          subActivity: true,
                        }
                      )
                    }
                    className={` ${
                      activeCard.includes(`subactivity_${e.id}`)
                        ? "bg-secondary-20"
                        : "bg-white"
                    } border-0 text-secondary-50 d-flex align-items-center gap-1 fs-12px text-secondary-50 px-2 py-1  rounded-4`}
                  >
                    {activeCard.includes(`subactivity_${e.id}`) ? (
                      <i className="icon-remove_circle_outline"></i>
                    ) : (
                      <i className="icon-add_circle_outline"></i>
                    )}
                    Sub-Activity
                  </button>
                </div>
              </div>

              {activeCard.includes(`subhead_${e.id}`) && (
                <>
                  <div className="border mx-2"></div>
                  <div className="consolidate-subhead-list px-2 pb-2">
                    {e.subheads.map((subhead, subheadIndex) => {
                      const isAccountDetailsOpen = subheadTables.includes(
                        `subhead_${subhead.id}_details`
                      );

                      return (
                      <div
                        className="consolidate-subhead-card"
                        key={subhead.id || subheadIndex}
                      >
                        <div className="consolidate-subhead-card__header">
                          <span className="consolidate-subhead-card__title">
                            Scheme Id {subhead.code}-{subhead.name}
                          </span>
                        </div>

                        <div className="consolidate-subhead-metrics">
                          <div className="consolidate-subhead-metric consolidate-subhead-metric--soft">
                            <span className="consolidate-subhead-metric__label">
                              Prov. Budget FY {selectedFyYear}
                            </span>
                            <span className="consolidate-subhead-metric__value text-success-100">
                              {formatAmount(subhead.provisional_budget)}
                            </span>
                          </div>
                          <div className="consolidate-subhead-metric consolidate-subhead-metric--soft">
                            <span className="consolidate-subhead-metric__label">
                              Released Budget FY {selectedFyYear}
                            </span>
                            <span className="consolidate-subhead-metric__value text-success-100">
                              {formatAmount(subhead.budget)}
                            </span>
                          </div>
                          <div className="consolidate-subhead-metric consolidate-subhead-metric--navy">
                            <span className="consolidate-subhead-metric__label">
                              Expense Budget FY {selectedFyYear}
                            </span>
                            <span className="consolidate-subhead-metric__value">
                              {formatAmount(
                                calculateExpense(subhead.tierFigures)
                              )}
                            </span>
                          </div>
                          <div className="consolidate-subhead-metric consolidate-subhead-metric--green">
                            <span className="consolidate-subhead-metric__label">
                              Balance FY {selectedFyYear}
                            </span>
                            <span className="consolidate-subhead-metric__value">
                              {formatAmount(
                                subhead.budget -
                                  calculateExpense(subhead.tierFigures)
                              )}
                            </span>
                          </div>
                        </div>

                        <div className="consolidate-subhead-table-wrap">
                          <table className="consolidate-subhead-table">
                            <colgroup>
                              <col style={{ width: "12%" }} />
                              <col style={{ width: "17.6%" }} />
                              <col style={{ width: "17.6%" }} />
                              <col style={{ width: "17.6%" }} />
                              <col style={{ width: "17.6%" }} />
                              <col style={{ width: "17.6%" }} />
                            </colgroup>
                            <thead>
                              <tr>
                                <th>Tier</th>
                                <th>Prov. Budget</th>
                                <th>Released</th>
                                <th>Allocation</th>
                                <th>Expense</th>
                                <th>Balance</th>
                              </tr>
                            </thead>
                            <tbody>
                              {subhead.tierFigures.map(
                                (tierFig, tierIndex) => (
                                  <tr key={tierIndex}>
                                    <td>
                                      <span className="consolidate-subhead-tier-badge">
                                        {tierFig?.department_level || "—"}
                                      </span>
                                    </td>
                                    <td className="is-amount">
                                      {formatAmount(
                                        tierFig?.provisional_budget
                                      )}
                                    </td>
                                    <td className="is-amount">
                                      {formatAmount(tierFig?.budget)}
                                    </td>
                                    <td className="is-amount">
                                      {formatAmount(
                                        tierFig?.allocated_budget
                                      )}
                                    </td>
                                    <td className="is-amount">
                                      {formatAmount(tierFig?.expenses)}
                                    </td>
                                    <td className="is-amount">
                                      {formatAmount(
                                        Number(tierFig?.budget || 0) -
                                          (Number(
                                            tierFig?.allocated_budget || 0
                                          ) +
                                            Number(tierFig?.expenses || 0))
                                      )}
                                    </td>
                                  </tr>
                                )
                              )}
                            </tbody>
                          </table>
                        </div>

                        <button
                          type="button"
                          className={`consolidate-account-details-btn${
                            isAccountDetailsOpen
                              ? " consolidate-account-details-btn--open"
                              : ""
                          }`}
                          onClick={() =>
                            handleSubheadTable(
                              `subhead_${subhead.id}_details`
                            )
                          }
                          aria-expanded={isAccountDetailsOpen}
                        >
                          <i
                            className={
                              isAccountDetailsOpen
                                ? "icon-remove_circle_outline"
                                : "icon-add_circle_outline"
                            }
                          />
                          <span>
                            {isAccountDetailsOpen
                              ? "Hide account details"
                              : "Show account details"}
                          </span>
                        </button>

                        <div
                          className={`consolidate-account-matrix-wrap ${
                            isAccountDetailsOpen ? "" : "d-none"
                          }`}
                        >
                          <div className="consolidate-account-matrix-scroll">
                            <table className="consolidate-account-matrix">
                              <thead>
                                <tr>
                                  <th className="col-t2-name">T2 Department</th>
                                  <th className="col-t2-account">T2 Account</th>
                                  <th className="col-child" colSpan={2}>
                                    Child departments (T3 / T4)
                                  </th>
                                </tr>
                                <tr className="consolidate-account-matrix__subhead">
                                  <th></th>
                                  <th>
                                    <div className="consolidate-account-matrix__cols">
                                      <span>Account Name</span>
                                      <span>Account No.</span>
                                      <span>Alloc. By T1</span>
                                      <span>Expense</span>
                                    </div>
                                  </th>
                                  <th colSpan={2}>
                                    <div className="consolidate-account-matrix__pair-head">
                                      <div className="consolidate-account-matrix__cols">
                                        <span>T3 Account Name</span>
                                        <span>Account No.</span>
                                        <span>Alloc. By T2</span>
                                        <span>Expense</span>
                                      </div>
                                      <div className="consolidate-account-matrix__cols">
                                        <span>T4 Account Name</span>
                                        <span>Account No.</span>
                                        <span>Alloc. By T3</span>
                                        <span>Expense</span>
                                      </div>
                                    </div>
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {(subhead?.departmentTierFigures || []).map(
                                  (deptTier, deptIndex) => {
                                    const t3List = deptTier.t3Departments || [];
                                    const t3TotalSanctioned = t3List.reduce(
                                      (sum, dept) =>
                                        sum + Number(dept?.sanctioned || 0),
                                      0
                                    );
                                    const t3TotalExpense = t3List.reduce(
                                      (sum, dept) =>
                                        sum + Number(dept?.expense || 0),
                                      0
                                    );

                                    return (
                                      <tr
                                        key={deptTier.id || deptIndex}
                                        className="consolidate-account-matrix__t2-row"
                                      >
                                        <td className="col-t2-name">
                                          <span className="consolidate-account-level-tag">
                                            T2
                                          </span>
                                          <strong>
                                            {deptTier?.name ||
                                              deptTier?.account_name ||
                                              "—"}
                                          </strong>
                                        </td>
                                        <td className="col-t2-account">
                                          <div className="consolidate-account-matrix__cols consolidate-account-matrix__cols--values">
                                            <span title={deptTier?.account_name || ""}>
                                              {deptTier?.account_name || "—"}
                                            </span>
                                            <span>{deptTier?.account_no || "—"}</span>
                                            <span className="is-amount">
                                              {formatAmount(deptTier?.sanctioned)}
                                            </span>
                                            <span className="is-amount">
                                              {formatAmount(deptTier?.expense)}
                                            </span>
                                          </div>
                                        </td>
                                        <td className="col-child" colSpan={2}>
                                          {expendAll ? (
                                            <>
                                              {t3List.length ? (
                                                t3List.map((t3Dept, t3Index) => {
                                                  const t4List =
                                                    t3Dept.t4Departments || [];
                                                  const t4TotalSanctioned =
                                                    t4List.reduce(
                                                      (sum, dept) =>
                                                        sum +
                                                        Number(
                                                          dept?.sanctioned || 0
                                                        ),
                                                      0
                                                    );
                                                  const t4TotalExpense =
                                                    t4List.reduce(
                                                      (sum, dept) =>
                                                        sum +
                                                        Number(
                                                          dept?.expense || 0
                                                        ),
                                                      0
                                                    );

                                                  return (
                                                    <div
                                                      className="consolidate-account-matrix__pair"
                                                      key={t3Dept.id || t3Index}
                                                    >
                                                      <div className="consolidate-account-matrix__t3-block">
                                                        <div className="consolidate-account-matrix__cols consolidate-account-matrix__cols--values">
                                                          <span
                                                            title={
                                                              t3Dept?.account_name ||
                                                              ""
                                                            }
                                                          >
                                                            <span className="consolidate-account-level-tag consolidate-account-level-tag--t3">
                                                              T3
                                                            </span>
                                                            {t3Dept?.account_name ||
                                                              "—"}
                                                          </span>
                                                          <span>
                                                            {t3Dept?.account_no ||
                                                              "—"}
                                                          </span>
                                                          <span className="is-amount">
                                                            {formatAmount(
                                                              t3Dept?.sanctioned
                                                            )}
                                                          </span>
                                                          <span className="is-amount">
                                                            {formatAmount(
                                                              t3Dept?.expense
                                                            )}
                                                          </span>
                                                        </div>
                                                      </div>
                                                      <div className="consolidate-account-matrix__t4-block">
                                                        {t4List.length ? (
                                                          <table className="consolidate-account-matrix__t4-table">
                                                            <tbody>
                                                              {t4List.map(
                                                                (
                                                                  t4Dept,
                                                                  t4Index
                                                                ) => (
                                                                  <tr
                                                                    key={
                                                                      t4Dept.id ||
                                                                      t4Index
                                                                    }
                                                                  >
                                                                    <td
                                                                      title={
                                                                        t4Dept?.account_name ||
                                                                        ""
                                                                      }
                                                                    >
                                                                      <span className="consolidate-account-level-tag consolidate-account-level-tag--t4">
                                                                        T4
                                                                      </span>
                                                                      {t4Dept?.account_name ||
                                                                        "—"}
                                                                    </td>
                                                                    <td>
                                                                      {t4Dept?.account_no ||
                                                                        "—"}
                                                                    </td>
                                                                    <td className="is-amount">
                                                                      {formatAmount(
                                                                        t4Dept?.sanctioned
                                                                      )}
                                                                    </td>
                                                                    <td className="is-amount">
                                                                      {formatAmount(
                                                                        t4Dept?.expense
                                                                      )}
                                                                    </td>
                                                                  </tr>
                                                                )
                                                              )}
                                                            </tbody>
                                                            <tfoot>
                                                              <tr>
                                                                <td colSpan={2}>
                                                                  Total T4
                                                                </td>
                                                                <td className="is-amount">
                                                                  {formatAmount(
                                                                    t4TotalSanctioned
                                                                  )}
                                                                </td>
                                                                <td className="is-amount">
                                                                  {formatAmount(
                                                                    t4TotalExpense
                                                                  )}
                                                                </td>
                                                              </tr>
                                                            </tfoot>
                                                          </table>
                                                        ) : (
                                                          <div className="consolidate-account-empty">
                                                            No T4
                                                          </div>
                                                        )}
                                                      </div>
                                                    </div>
                                                  );
                                                })
                                              ) : (
                                                <div className="consolidate-account-empty">
                                                  No T3 accounts
                                                </div>
                                              )}
                                              {t3List.length ? (
                                                <div className="consolidate-account-matrix__total">
                                                  <span>Total T3</span>
                                                  <span className="is-amount">
                                                    {formatAmount(
                                                      t3TotalSanctioned
                                                    )}
                                                  </span>
                                                  <span className="is-amount">
                                                    {formatAmount(t3TotalExpense)}
                                                  </span>
                                                </div>
                                              ) : null}
                                            </>
                                          ) : (
                                            <div className="consolidate-account-empty">
                                              Use Expand All Child in header to
                                              show all T3 / T4 together
                                            </div>
                                          )}
                                        </td>
                                      </tr>
                                    );
                                  }
                                )}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          ))}
            </div>
          </div>
      )}
    </div>
  );
};

export default ConsolidateDashboardReport;
